import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import { worksheetEmailTemplateVariables } from '../../../../config/worksheetEmail';
import { ButtonUI, DialogUI, FileUploadUI, FormLabelUI, InputUI, RichTextEditorUI, SelectUI } from '../../../Interface';

const normalizeEmailField = (value) => {
  if (!value) return null;

  const items = String(value)
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length === 0) return null;
  if (items.length === 1) return items[0];
  return items;
};

const getUserEmail = (user) => user?.email ?? user?.mail ?? user?.emailAddress ?? user?.userEmail ?? '';

const createInitialState = ({ config, emailTemplates, initialEmailValues, recipientAndPhotosOnly, user }) => {
  const assignedTemplate = emailTemplates.find((template) => template.id === initialEmailValues?.templateId) ?? null;
  const initialTemplate = assignedTemplate ?? (recipientAndPhotosOnly ? null : emailTemplates?.[0] ?? null);

  return {
    selectedTemplateId: initialTemplate?.id ?? null,
    to: initialEmailValues?.to ?? '',
    cc: config?.emailSendCopyToSender ? getUserEmail(user) : '',
    bcc: '',
    replyTo: config?.defaultReplyToAddress ?? config?.emailSenderAddress ?? '',
    subject: initialTemplate?.subject ?? '',
    body: initialTemplate?.body ?? '',
    additionalAttachments: [],
  };
};

const WorksheetEmailDialog = ({
  config,
  emailTemplates = [],
  fixedAttachments = [],
  initialEmailValues = null,
  onClose,
  onSend,
  open,
  recipientAndPhotosOnly = false,
  user,
}) => {
  const [formState, setFormState] = useState(() =>
    createInitialState({ config, emailTemplates, initialEmailValues, recipientAndPhotosOnly, user })
  );
  const [uploadAreaKey, setUploadAreaKey] = useState(0);
  const [activeField, setActiveField] = useState('body');
  const subjectInputRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const subjectSelectionRef = useRef({ start: 0, end: 0 });
  const templateOptions = useMemo(
    () =>
      emailTemplates.map((template) => ({
        value: template.id,
        label: [template.name, template.languageCode].filter(Boolean).join(' - '),
      })),
    [emailTemplates]
  );

  useEffect(() => {
    if (!open) return;

    setFormState(createInitialState({ config, emailTemplates, initialEmailValues, recipientAndPhotosOnly, user }));
    setUploadAreaKey((current) => current + 1);
  }, [config, emailTemplates, initialEmailValues, open, recipientAndPhotosOnly, user]);

  const handleFieldChange = (field) => (event) => {
    setFormState((current) => ({ ...current, [field]: event?.target?.value ?? '' }));
  };

  const handleTemplateChange = (event) => {
    const templateId = event?.target?.value ?? null;
    const selectedTemplate = emailTemplates.find((template) => Number(template.id) === Number(templateId)) ?? null;
    setFormState((current) => ({
      ...current,
      selectedTemplateId: templateId,
      subject: selectedTemplate?.subject ?? '',
      body: selectedTemplate?.body ?? '',
    }));
  };

  const syncSubjectSelection = () => {
    const input = subjectInputRef.current;
    if (!input) return;

    subjectSelectionRef.current = {
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? input.selectionStart ?? 0,
    };
  };

  const insertIntoSubject = (token) => {
    const currentValue = formState.subject ?? '';
    const insertion = `${token} `;
    const start = subjectSelectionRef.current.start ?? currentValue.length;
    const end = subjectSelectionRef.current.end ?? start;
    const nextValue = currentValue.slice(0, start) + insertion + currentValue.slice(end);
    const nextPosition = start + insertion.length;

    setFormState((current) => ({ ...current, subject: nextValue }));
    subjectSelectionRef.current = { start: nextPosition, end: nextPosition };
    requestAnimationFrame(() => {
      subjectInputRef.current?.focus();
      subjectInputRef.current?.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const handleInsertVariable = (token) => {
    if (activeField === 'subject') {
      insertIntoSubject(token);
      return;
    }
    bodyEditorRef.current?.insertText?.(`${token} `);
  };

  const handleSend = async () => {
    if (!formState.to?.trim()) {
      addNotification({ content: 'A címzett megadása kötelező.', type: NotificationType.WARNING });
      return;
    }
    if (!formState.selectedTemplateId) {
      addNotification({ content: 'Az e-mail sablon kiválasztása kötelező.', type: NotificationType.WARNING });
      return;
    }
    if (!formState.body?.trim()) {
      addNotification({ content: 'Az e-mail szövege nem lehet üres.', type: NotificationType.WARNING });
      return;
    }

    await onSend({
      emailTo: normalizeEmailField(formState.to),
      emailCc: normalizeEmailField(formState.cc),
      emailBcc: normalizeEmailField(formState.bcc),
      replyTo: normalizeEmailField(formState.replyTo),
      subject: formState.subject,
      body: formState.body,
      templateId: formState.selectedTemplateId,
      files: (formState.additionalAttachments ?? []).map((attachment) => attachment.file).filter(Boolean),
    });
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent="Munkalap küldése e-mailben"
      maxWidth="lg"
      fullWidth
      closeBtn={false}
      sx={{ '& .MuiDialog-container': { '& .MuiPaper-root': { width: '100%', maxWidth: '1100px' } } }}
    >
      <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SelectUI
          disabled={recipientAndPhotosOnly}
          value={formState.selectedTemplateId ?? ''}
          onChange={handleTemplateChange}
          option={templateOptions}
          label={<FormLabelUI text="Sablon" />}
        />

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
          <InputUI value={formState.to} onChange={handleFieldChange('to')} label={<FormLabelUI text="Címzett" />} variant="standard" />
          <InputUI disabled={recipientAndPhotosOnly} value={formState.replyTo} onChange={handleFieldChange('replyTo')} label={<FormLabelUI text="Válaszcím" />} variant="standard" />
          <InputUI disabled={recipientAndPhotosOnly} value={formState.cc} onChange={handleFieldChange('cc')} label={<FormLabelUI text="Másolat" />} variant="standard" />
          <InputUI disabled={recipientAndPhotosOnly} value={formState.bcc} onChange={handleFieldChange('bcc')} label={<FormLabelUI text="Titkos másolat" />} variant="standard" />
        </Box>

        <InputUI
          disabled={recipientAndPhotosOnly}
          inputRef={subjectInputRef}
          value={formState.subject}
          onChange={handleFieldChange('subject')}
          onFocus={() => {
            setActiveField('subject');
            syncSubjectSelection();
          }}
          onClick={syncSubjectSelection}
          onKeyUp={syncSubjectSelection}
          onSelect={syncSubjectSelection}
          label={<FormLabelUI text="Tárgy" />}
          variant="standard"
        />

        <RichTextEditorUI
          readOnly={recipientAndPhotosOnly}
          ref={bodyEditorRef}
          label={<FormLabelUI text="Szöveg" />}
          value={formState.body}
          onChange={(value) => setFormState((current) => ({ ...current, body: value ?? '' }))}
          onFocus={() => setActiveField('body')}
          minHeight={340}
        />

        {!recipientAndPhotosOnly && <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Sablonváltozók</Typography>
          <Box sx={{ mt: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
            {worksheetEmailTemplateVariables.map((variable) => (
              <ButtonBase
                key={variable.token}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleInsertVariable(variable.token);
                }}
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, justifyContent: 'space-between' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{variable.token}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{variable.description}</Typography>
              </ButtonBase>
            ))}
          </Box>
        </Box>}

        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Csatolmányok</Typography>
          <Box sx={{ mt: 1.5 }}>
            <FileUploadUI
              key={uploadAreaKey}
              accept={recipientAndPhotosOnly ? { 'image/*': [] } : undefined}
              onFilesChange={(files) => setFormState((current) => ({ ...current, additionalAttachments: files ?? [] }))}
            />
          </Box>
          {fixedAttachments.length > 0 && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              Automatikus csatolmány: {fixedAttachments.map((attachment) => attachment.name).join(', ')}
            </Typography>
          )}
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
            Kiválasztott plusz csatolmányok: {formState.additionalAttachments.length}
          </Typography>
        </Box>

        <Box display="flex" gap={2} justifyContent="center" pt={1}>
          <ButtonUI text="Küldés" color="green" type="button" onClick={handleSend} />
          <ButtonUI text="Mégsem" color="blue" type="button" onClick={onClose} />
        </Box>
      </Box>
    </DialogUI>
  );
};

export default WorksheetEmailDialog;
